import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Banknote, ExternalLink, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function ConnectStripeButton({ providerId, isOwner }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!isOwner) return;
    try {
      const { data } = await api.get("/payments/connect/status");
      setStatus(data);
    } catch (e) {
      // 404 means user isn't a provider — hide
      // Other errors → fall back to "unsupported" UI so we don't crash
      setStatus({ supportsConnect: false, connected: false });
    }
  };
  useEffect(() => { load(); }, [providerId]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/connect/onboard", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe Connect unavailable. Please try again later.");
    } finally { setBusy(false); }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/connect/dashboard");
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open dashboard");
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { await load(); toast.success("Refreshed"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Stripe account from Cauldron? Earnings already paid out remain in your bank — you can re-connect anytime.")) return;
    setBusy(true);
    try {
      await api.post("/payments/connect/disconnect");
      toast.success("Disconnected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    } finally { setBusy(false); }
  };

  if (!isOwner || !status) return null;

  // Case 1: Connect not enabled platform-wide
  if (!status.supportsConnect && !status.connected) {
    return (
      <div className="parchment p-4 mt-4 flex items-start gap-3 border-secondary/20" data-testid="connect-stripe-disabled">
        <AlertCircle size={18} strokeWidth={1.25} className="text-secondary mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="label-mystic">Direct Payouts (Coming Soon)</div>
          <p className="text-foreground/75 mt-1">
            Stripe Connect onboarding will be available shortly. In the meantime, your earnings accumulate in your wallet and can be cashed out via Bank, Venmo, PayPal, Cash App or Crypto from the <a href="/cashout" className="text-secondary underline">Cashout page</a>.
          </p>
        </div>
      </div>
    );
  }

  // Case 2: Connected + fully enabled
  if (status.connected && status.chargesEnabled && status.payoutsEnabled) {
    return (
      <div className="parchment p-4 mt-4 flex items-center justify-between gap-3 flex-wrap" data-testid="connect-stripe-active">
        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
          <div className="w-10 h-10 rounded-sm bg-secondary/10 border border-secondary/40 flex items-center justify-center">
            <Banknote size={18} strokeWidth={1.25} className="text-secondary" />
          </div>
          <div>
            <div className="label-mystic">Stripe Connect · Active</div>
            <div className="text-sm text-foreground/80">Receive bookings, services and product earnings directly to your bank.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={openDashboard} disabled={busy}
            className="border border-secondary text-secondary px-4 py-2 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2"
            data-testid="connect-stripe-dashboard-btn">
            Dashboard <ExternalLink size={12} strokeWidth={1.25} />
          </button>
          <button onClick={refresh} disabled={busy}
            className="border border-secondary/30 text-foreground/70 px-3 py-2 rounded-sm uppercase tracking-wider text-xs"
            data-testid="connect-stripe-refresh-btn" title="Refresh status">
            <RefreshCw size={14} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
    );
  }

  // Case 3: Connected but capabilities pending OR not yet connected
  const isPartiallyConnected = status.connected && (!status.chargesEnabled || !status.payoutsEnabled);
  let ctaLabel;
  let label;
  let body;
  if (isPartiallyConnected) {
    ctaLabel = status.detailsSubmitted ? "Refresh Status" : "Resume Stripe Onboarding";
    label = "Stripe Connect · Almost there";
    body = status.detailsSubmitted
      ? "Stripe is reviewing your details. This usually takes a few minutes — tap Refresh to check status."
      : "Finish your onboarding to start receiving Direct Payouts.";
  } else {
    ctaLabel = "Connect Stripe Payouts";
    label = "Direct Payouts";
    body = "Connect your bank account in 2 minutes to receive booking & service earnings directly.";
  }

  return (
    <div className="parchment p-4 mt-4 flex items-center justify-between gap-3 flex-wrap" data-testid="connect-stripe-cta">
      <div className="flex items-center gap-3 flex-1 min-w-[240px]">
        <div className="w-10 h-10 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center">
          <Banknote size={18} strokeWidth={1.25} className="text-primary" />
        </div>
        <div>
          <div className="label-mystic">{label}</div>
          <div className="text-sm text-foreground/80">{body}</div>
        </div>
      </div>
      <div className="flex gap-2">
        {isPartiallyConnected && status.detailsSubmitted ? (
          <button onClick={refresh} disabled={busy}
            className="border border-primary text-primary px-4 py-2 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2"
            data-testid="connect-stripe-refresh-btn">
            <RefreshCw size={12} strokeWidth={1.5} className={busy ? "animate-spin" : ""} /> {ctaLabel}
          </button>
        ) : (
          <button onClick={startOnboarding} disabled={busy}
            className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs whitespace-nowrap"
            data-testid="connect-stripe-onboard-btn">
            {busy ? "Loading..." : ctaLabel}
          </button>
        )}
        {isPartiallyConnected && (
          <button onClick={disconnect} disabled={busy}
            className="border border-destructive/40 text-destructive/80 hover:text-destructive px-3 py-2 rounded-sm uppercase tracking-wider text-xs"
            data-testid="connect-stripe-disconnect-btn" title="Disconnect">
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
